import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  SimpleButton,
  LoadingButton,
  Input,
  TextArea,
  Select,
  Modal,
  Toast,
  Spinner,
  useStateContext,
} from "@veda/dashboard-ui";
import { BsArrowLeft, BsPlus, BsPencil, BsTrash, BsBoxArrowUpRight, BsCheckCircle, BsFilePdf } from "react-icons/bs";
import { usePppApi } from "../services/pppApi";
import { useAuthUser } from "../hooks/useAuthUser";

const SOGLIA_MINIMA = 150000;

const STATO_CFG = {
  bozza:      { key: "ppp_prog_stato_bozza",      cls: "bg-gray-100 text-gray-700" },
  approvato:  { key: "ppp_prog_stato_approvato",  cls: "bg-blue-100 text-blue-700" },
  pubblicato: { key: "ppp_prog_stato_pubblicato", cls: "bg-green-100 text-green-700" },
  archiviato: { key: "ppp_prog_stato_archiviato", cls: "bg-gray-200 text-gray-500" },
};

const FONTI = [
  { value: "bilancio_comunale", labelKey: "ppp_fonte_bilancio_comunale" },
  { value: "mutuo",             labelKey: "ppp_fonte_mutuo" },
  { value: "fondi_europei",     labelKey: "ppp_fonte_fondi_europei" },
  { value: "fondi_regionali",   labelKey: "ppp_fonte_fondi_regionali" },
  { value: "contributo_privato",labelKey: "ppp_fonte_contributo_privato" },
  { value: "altro",             labelKey: "ppp_fonte_altro" },
];

const ANNUALITA = [
  { value: 1, labelKey: "ppp_prog_int_annualita_1" },
  { value: 2, labelKey: "ppp_prog_int_annualita_2" },
  { value: 3, labelKey: "ppp_prog_int_annualita_3" },
];

function formatEuro(val) {
  if (!val) return "—";
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(val));
}

function StatoBadge({ stato, _locate }) {
  const cfg = STATO_CFG[stato] ?? { key: stato, cls: "" };
  return (
    <span className={`px-2 py-0.5 rounded text-sm font-medium ${cfg.cls}`}>
      {_locate(cfg.key) ?? stato}
    </span>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
      <span className="text-sm text-gray-900 dark:text-white mt-0.5">{value || "—"}</span>
    </div>
  );
}

// ─── Form intervento ──────────────────────────────────────────────────────────

function InterventoForm({ initialData, procedimenti, onSave, onCancel, saving, errors, _locate }) {
  const empty = { titolo: "", annualita: 1, importo: "", fonteFinanziamento: "bilancio_comunale", codiceIntervento: "", descrizione: "", settore: "", noteFinanziamento: "", procedimentoId: "" };
  const [form, setForm] = useState(initialData ?? empty);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const importoNum = parseFloat(form.importo) || 0;
  const sogliaWarn = importoNum > 0 && importoNum < SOGLIA_MINIMA;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Input label={_locate("ppp_prog_int_titolo")} required value={form.titolo} onChange={set("titolo")} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {_locate("ppp_prog_int_annualita")}
          </label>
          <Select
            value={form.annualita}
            onChange={(e) => setForm((f) => ({ ...f, annualita: parseInt(e.target.value) }))}
            options={ANNUALITA.map((a) => ({ value: a.value, label: _locate(a.labelKey) }))}
          />
        </div>
        <div>
          <Input
            label={_locate("ppp_prog_int_importo")}
            required
            type="number"
            min={0}
            step={1000}
            value={form.importo}
            onChange={set("importo")}
          />
          {sogliaWarn && (
            <p className="text-xs text-amber-600 mt-1">{_locate("ppp_prog_soglia_warning")}</p>
          )}
          <p className="text-xs text-gray-400 mt-0.5">{_locate("ppp_prog_int_importo_hint")}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {_locate("ppp_prog_int_fonte")}
          </label>
          <Select
            value={form.fonteFinanziamento}
            onChange={(e) => setForm((f) => ({ ...f, fonteFinanziamento: e.target.value }))}
            options={FONTI.map((f) => ({ value: f.value, label: _locate(f.labelKey) }))}
          />
        </div>
        <div>
          <Input label={_locate("ppp_prog_int_settore")} value={form.settore} onChange={set("settore")} />
        </div>
        <div>
          <Input label={_locate("ppp_prog_int_codice")} value={form.codiceIntervento} onChange={set("codiceIntervento")} />
        </div>
        <div className="col-span-2">
          <TextArea label={_locate("ppp_prog_int_descrizione")} value={form.descrizione} onChange={set("descrizione")} rows={2} />
        </div>
        <div className="col-span-2">
          <TextArea label={_locate("ppp_prog_int_note_fin")} value={form.noteFinanziamento} onChange={set("noteFinanziamento")} rows={2} />
        </div>
      </div>
      {errors && errors.length > 0 && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3">
          <ul className="text-sm text-red-700 space-y-0.5">
            {errors.map((e, i) => <li key={i}>• {e}</li>)}
          </ul>
        </div>
      )}
      <div className="flex gap-2 justify-end pt-2 border-t border-gray-100">
        <SimpleButton customFunc={onCancel} text={_locate("ppp_prog_annulla")} />
        <LoadingButton
          isLoading={saving}
          customFunc={form.titolo && form.importo ? () => onSave(form) : undefined}
          text={_locate("ppp_prog_int_salva")}
        />
      </div>
    </div>
  );
}

// ─── Pagina principale ────────────────────────────────────────────────────────

export default function ProgrammazioneDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { _locate, addModal, addToast } = useStateContext();
  const api = usePppApi();
  const { can } = useAuthUser();

  const [programma, setProgramma] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Form intervento (null = chiuso, {} = nuovo, {...} = modifica)
  const [interventoForm, setInterventoForm] = useState(null);
  const [interventoTarget, setInterventoTarget] = useState(null); // ID in modifica
  const [interventoErrors, setInterventoErrors] = useState(null);

  // Modal approva / pubblica
  const [approvaModal, setApprovaModal] = useState(false);
  const [pubblicaModal, setPubblicaModal] = useState(false);
  const [approvaForm, setApprovaForm] = useState({ numeroDelibera: "", dataApprovazione: "" });
  const [pubblicaForm, setPubblicaForm] = useState({ urlBdncp: "" });

  const refresh = useCallback(() => {
    api.getProgramma(id, {
      onSuccess: (data) => { setProgramma(data); setLoading(false); },
      onError: () => setLoading(false),
    });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { refresh(); }, [refresh]);

  if (loading) return <div className="p-8 flex justify-center"><Spinner /></div>;
  if (!programma) return <div className="p-8 text-gray-500">{_locate("ppp_non_trovato")}</div>;

  const canEdit = programma.stato !== "pubblicato" && can("ppp.programmazione.edit");

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleSalva = () => {
    setSaving(true);
    api.aggiornaProgramma(id, editForm, {
      onSuccess: () => {
        setSaving(false);
        setEditMode(false);
        addToast(<Toast duration={4} content={_locate("ppp_prog_aggiornato")} />);
        refresh();
      },
      onError: () => setSaving(false),
    });
  };

  const handleApprova = () => {
    setSaving(true);
    api.approvaProgramma(id, approvaForm, {
      onSuccess: () => {
        setSaving(false);
        setApprovaModal(false);
        addToast(<Toast duration={4} content={_locate("ppp_prog_approvato")} />);
        refresh();
      },
      onError: () => setSaving(false),
    });
  };

  const handlePubblica = () => {
    setSaving(true);
    api.pubblicaProgramma(id, pubblicaForm, {
      onSuccess: () => {
        setSaving(false);
        setPubblicaModal(false);
        addToast(<Toast duration={4} content={_locate("ppp_prog_pubblicato")} />);
        refresh();
      },
      onError: () => setSaving(false),
    });
  };

  const handleSalvaIntervento = (form) => {
    setSaving(true);
    setInterventoErrors(null);
    const cb = {
      onSuccess: () => {
        setSaving(false);
        setInterventoForm(null);
        setInterventoTarget(null);
        setInterventoErrors(null);
        addToast(<Toast duration={4} content={interventoTarget
          ? _locate("ppp_prog_intervento_aggiornato")
          : _locate("ppp_prog_intervento_aggiunto")}
        />);
        refresh();
      },
      onFormError: (body) => {
        setSaving(false);
        const msgs = body?.errors
          ? Object.values(body.errors).flat()
          : [body?.message || "Errore durante il salvataggio."];
        setInterventoErrors(msgs);
      },
    };

    if (interventoTarget) {
      api.aggiornaIntervento(id, interventoTarget, form, cb);
    } else {
      api.creaIntervento(id, form, cb);
    }
  };

  const handleEliminaIntervento = (intervento) => {
    addModal(
      <Modal
        title={_locate("ppp_prog_intervento_elimina")}
        content={`${_locate("ppp_prog_intervento_elimina_msg")} (${intervento.titolo})`}
        confirm={_locate("ppp_elimina")}
        style="danger"
        onClick={() => {
          api.eliminaIntervento(id, intervento.id, {
            onSuccess: () => {
              addToast(<Toast duration={4} content={_locate("ppp_prog_intervento_eliminato")} />);
              refresh();
            },
          });
        }}
      />
    );
  };

  // ─── Raggruppamento interventi per annualità ─────────────────────────────────

  const byAnno = [1, 2, 3].map((a) => ({
    anno: a,
    label: _locate(`ppp_prog_int_annualita_${a}`),
    interventi: (programma.interventi ?? []).filter((i) => i.annualita === a),
  }));

  const totaleGenerale = (programma.interventi ?? []).reduce((s, i) => s + parseFloat(i.importo || 0), 0);

  const FONTE_LABELS = Object.fromEntries(FONTI.map((f) => [f.value, _locate(f.labelKey)]));

  return (
    <div className="p-4">
      {/* Breadcrumb */}
      <button
        onClick={() => navigate("/ppp/programmazione")}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <BsArrowLeft size={13} />
        {_locate("ppp_prog_torna_lista")}
      </button>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              {programma.enteConcedente} — {programma.annoRiferimento}–{programma.annoRiferimento + 2}
            </h1>
            <StatoBadge stato={programma.stato} _locate={_locate} />
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && !editMode && (
            <SimpleButton
              customFunc={() => {
                setEditForm({
                  enteConcedente: programma.enteConcedente,
                  annoRiferimento: programma.annoRiferimento,
                  note: programma.note ?? "",
                  urlBdncp: programma.urlBdncp ?? "",
                });
                setEditMode(true);
              }}
              text={_locate("ppp_prog_modifica")}
            />
          )}
          {programma.stato === "bozza" && can("ppp.programmazione.approve") && (
            <SimpleButton customFunc={() => setApprovaModal(true)} text={_locate("ppp_prog_approva")} />
          )}
          {programma.stato === "approvato" && can("ppp.programmazione.publish") && (
            <SimpleButton customFunc={() => setPubblicaModal(true)} text={_locate("ppp_prog_pubblica")} />
          )}
        </div>
      </div>

      {/* Info card */}
      {editMode ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 mb-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label={_locate("ppp_prog_ente")}
              required
              value={editForm.enteConcedente}
              onChange={(e) => setEditForm((f) => ({ ...f, enteConcedente: e.target.value }))}
            />
            <Input
              label={_locate("ppp_prog_anno")}
              required
              type="number"
              value={editForm.annoRiferimento}
              onChange={(e) => setEditForm((f) => ({ ...f, annoRiferimento: parseInt(e.target.value) || f.annoRiferimento }))}
            />
            <div className="col-span-2">
              <Input
                label={_locate("ppp_prog_url_bdncp")}
                value={editForm.urlBdncp}
                onChange={(e) => setEditForm((f) => ({ ...f, urlBdncp: e.target.value }))}
              />
            </div>
            <div className="col-span-2">
              <TextArea
                label={_locate("ppp_prog_note")}
                value={editForm.note}
                onChange={(e) => setEditForm((f) => ({ ...f, note: e.target.value }))}
                rows={3}
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-2 border-t border-gray-100">
            <SimpleButton customFunc={() => setEditMode(false)} text={_locate("ppp_prog_annulla")} />
            <LoadingButton isLoading={saving} customFunc={handleSalva} text={_locate("ppp_prog_salva")} />
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 mb-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
            <InfoRow label={_locate("ppp_prog_ente")} value={programma.enteConcedente} />
            <InfoRow label={_locate("ppp_prog_anno")} value={`${programma.annoRiferimento}–${programma.annoRiferimento + 2}`} />
            {programma.numeroDelibera && (
              <InfoRow label={_locate("ppp_prog_delibera")} value={`n. ${programma.numeroDelibera}`} />
            )}
            {programma.dataApprovazione && (
              <InfoRow
                label={_locate("ppp_prog_data_approvazione")}
                value={new Date(programma.dataApprovazione).toLocaleDateString("it-IT")}
              />
            )}
            {programma.approvatoDaNome && (
              <InfoRow label={_locate("ppp_prog_approvato_da")} value={programma.approvatoDaNome} />
            )}
            <div className="flex flex-col">
              <span className="text-xs text-gray-500 uppercase tracking-wide">{_locate("ppp_prog_url_bdncp")}</span>
              {programma.urlBdncp ? (
                <a
                  href={programma.urlBdncp}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-0.5"
                >
                  {_locate("ppp_prog_apri_bdncp")} <BsBoxArrowUpRight size={11} />
                </a>
              ) : (
                <span className="text-sm text-gray-400 mt-0.5">{_locate("ppp_prog_non_pubblicato")}</span>
              )}
            </div>
            {programma.note && (
              <div className="col-span-2 sm:col-span-3">
                <InfoRow label={_locate("ppp_prog_note")} value={programma.note} />
              </div>
            )}
          </div>
          {programma.numeroDelibera && (
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-end">
              <a
                href={api.urlDeliberaPdf(id)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-800 hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                <BsFilePdf size={15} />
                {_locate("ppp_prog_scarica_delibera")}
              </a>
            </div>
          )}
        </div>
      )}

      {/* Interventi */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {_locate("ppp_prog_interventi_titolo")}
          <span className="ml-2 text-sm font-normal text-gray-500">
            ({(programma.interventi ?? []).length} — {formatEuro(totaleGenerale)})
          </span>
        </h2>
        {canEdit && !interventoForm && (
          <SimpleButton
            customFunc={() => { setInterventoForm({}); setInterventoTarget(null); setInterventoErrors(null); }}
            text={_locate("ppp_prog_intervento_nuovo")}
          />
        )}
      </div>

      {/* Form nuovo/modifica intervento */}
      {interventoForm !== null && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
            {interventoTarget ? _locate("ppp_prog_intervento_modifica") : _locate("ppp_prog_intervento_nuovo")}
          </h3>
          <InterventoForm
            initialData={interventoForm}
            onSave={handleSalvaIntervento}
            onCancel={() => { setInterventoForm(null); setInterventoTarget(null); setInterventoErrors(null); }}
            saving={saving}
            errors={interventoErrors}
            _locate={_locate}
          />
        </div>
      )}

      {/* Lista interventi per annualità */}
      {(programma.interventi ?? []).length === 0 ? (
        <p className="text-sm text-gray-500">{_locate("ppp_prog_interventi_vuoti")}</p>
      ) : (
        <div className="space-y-6">
          {byAnno.filter((g) => g.interventi.length > 0).map((gruppo) => (
            <div key={gruppo.anno}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{gruppo.label}</span>
                <span className="text-xs text-gray-400">
                  ({formatEuro(gruppo.interventi.reduce((s, i) => s + parseFloat(i.importo || 0), 0))})
                </span>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                {gruppo.interventi.map((intervento) => (
                  <div key={intervento.id} className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {intervento.titolo}
                        </span>
                        {intervento.codiceIntervento && (
                          <span className="text-xs text-gray-400">[{intervento.codiceIntervento}]</span>
                        )}
                        {parseFloat(intervento.importo) < SOGLIA_MINIMA && (
                          <span className="text-xs text-amber-600 font-medium">⚠ &lt;€150k</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                        <span>{formatEuro(intervento.importo)}</span>
                        <span>·</span>
                        <span>{FONTE_LABELS[intervento.fonteFinanziamento] ?? intervento.fonteFinanziamento}</span>
                        {intervento.settore && <><span>·</span><span>{intervento.settore}</span></>}
                        {intervento.procedimentoTitolo && (
                          <><span>·</span>
                          <span className="text-blue-600 flex items-center gap-0.5">
                            <BsCheckCircle size={11} /> {intervento.procedimentoTitolo}
                          </span></>
                        )}
                      </div>
                    </div>
                    {canEdit && (
                      <div className="flex gap-1 ml-4 shrink-0">
                        <button
                          onClick={() => {
                            setInterventoErrors(null);
                            setInterventoTarget(intervento.id);
                            setInterventoForm({
                              titolo: intervento.titolo,
                              annualita: intervento.annualita,
                              importo: intervento.importo,
                              fonteFinanziamento: intervento.fonteFinanziamento,
                              codiceIntervento: intervento.codiceIntervento ?? "",
                              descrizione: intervento.descrizione ?? "",
                              settore: intervento.settore ?? "",
                              noteFinanziamento: intervento.noteFinanziamento ?? "",
                              procedimentoId: intervento.procedimentoId ?? "",
                            });
                          }}
                          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
                        >
                          <BsPencil size={13} />
                        </button>
                        <button
                          onClick={() => handleEliminaIntervento(intervento)}
                          className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500"
                        >
                          <BsTrash size={13} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      </div>{/* fine card principale */}

      {/* Modal approva */}
      {approvaModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {_locate("ppp_prog_approva_titolo")}
            </h3>
            <div className="space-y-4">
              <Input
                label={_locate("ppp_prog_numero_delibera")}
                required
                value={approvaForm.numeroDelibera}
                onChange={(e) => setApprovaForm((f) => ({ ...f, numeroDelibera: e.target.value }))}
              />
              <Input
                label={_locate("ppp_prog_data_delibera")}
                required
                type="date"
                value={approvaForm.dataApprovazione}
                onChange={(e) => setApprovaForm((f) => ({ ...f, dataApprovazione: e.target.value }))}
              />
            </div>
            <div className="flex gap-2 justify-end mt-5 pt-4 border-t border-gray-100">
              <SimpleButton customFunc={() => setApprovaModal(false)} text={_locate("ppp_prog_annulla")} />
              <LoadingButton
                isLoading={saving}
                customFunc={approvaForm.numeroDelibera && approvaForm.dataApprovazione ? handleApprova : undefined}
                text={_locate("ppp_prog_conferma_approva")}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal pubblica */}
      {pubblicaModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {_locate("ppp_prog_pubblica_titolo")}
            </h3>
            <Input
              label={_locate("ppp_prog_url_bdncp_label")}
              value={pubblicaForm.urlBdncp}
              onChange={(e) => setPubblicaForm((f) => ({ ...f, urlBdncp: e.target.value }))}
            />
            <div className="flex gap-2 justify-end mt-5 pt-4 border-t border-gray-100">
              <SimpleButton customFunc={() => setPubblicaModal(false)} text={_locate("ppp_prog_annulla")} />
              <LoadingButton
                isLoading={saving}
                customFunc={handlePubblica}
                text={_locate("ppp_prog_conferma_pubblica")}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
